import React, { createContext, useContext, useState, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material';

const ThemeContext = createContext({ mode: 'light', toggleMode: () => {} });
export const useThemeMode = () => useContext(ThemeContext);

const buildTheme = mode => {
  const dark = mode === 'dark';

  return createTheme({
    typography: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      h4: { fontWeight: 800 },
      h5: { fontWeight: 800 },
      h6: { fontWeight: 700 },
      subtitle1: { fontWeight: 700 },
    },
    palette: {
      mode,
      primary: { main: '#3b82f6' },
      background: {
        default: dark ? '#0d1117' : '#f0f4f8',
        paper:   dark ? '#161b22' : '#ffffff',
      },
      text: {
        primary:   dark ? '#e6edf3' : '#1e3a5f',
        secondary: dark ? '#8b949e' : '#6b778c',
      },
      divider: dark ? 'rgba(255,255,255,0.07)' : 'rgba(47,99,173,0.09)',
    },
    shape: { borderRadius: 12 },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: dark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(47,99,173,0.09)',
            boxShadow: dark
              ? '0 2px 16px rgba(0,0,0,0.4)'
              : '0 2px 12px rgba(0,0,0,0.04)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontFamily: '"Inter","Segoe UI",sans-serif', fontWeight: 600 },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontFamily: '"Inter","Segoe UI",sans-serif',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 8,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            fontFamily: '"Inter","Segoe UI",sans-serif',
            textTransform: 'none',
            fontWeight: 600,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: { fontWeight: 700 },
        },
      },
    },
  });
};

export function AppThemeProvider({ children }) {
  const stored = localStorage.getItem('hoa-theme') || 'light';
  const [mode, setMode] = useState(stored);

  const toggleMode = () => {
    setMode(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('hoa-theme', next);
      return next;
    });
  };

  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleMode }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
